import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { t, getCurrentLanguage } from '../utils/translations';
import api from './api';
import { CardLoader } from './Loader';

const ODDSection = () => {
  const [oddSection, setOddSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    // Écouter l'événement de changement de langue
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Récupérer la dernière section ODD active
  useEffect(() => {
    const fetchODDSection = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/odd-sections/');
        
        if (response.data && response.data.length > 0) {
          // Récupérer seulement le dernier élément actif
          const lastActiveSection = response.data
            .filter(section => section.is_active)
            .sort((a, b) => b.order - a.order || new Date(b.created_at) - new Date(a.created_at))[0];
          
          setOddSection(lastActiveSection);
          setError(null);
        } else {
          setError(t('news.noOddSection'));
        }
      } catch (err) {
        setError(t('news.oddError'));
      } finally {
        setLoading(false);
      }
    };

    fetchODDSection();
  }, []);

  // Loader pendant le chargement
  if (loading) {
    return (
      <section className="w-full relative bg-gradient-to-br from-[#07D1EB] via-[#089556] to-[#0ba8c7] mt-8 sm:mt-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 relative z-10">
          <CardLoader className="bg-white/95 backdrop-blur-sm rounded-lg" />
        </div>
      </section>
    );
  }

  // Affichage d'erreur
  if (error || !oddSection) {
    return (
      <section className="w-full relative bg-gradient-to-br from-[#07D1EB] via-[#089556] to-[#0ba8c7] mt-8 sm:mt-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:mt-8 relative z-10">
          <div className="bg-white/95 backdrop-blur-sm p-6 rounded-lg text-center">
            <div className="h-12 w-12 text-red-500 mx-auto mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-600 text-lg font-medium mb-2">{t('news.loadingError')}</p>
            <p className="text-gray-500 text-sm">{error || t('news.oddErrorDesc')}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 bg-[#089556] hover:bg-[#07D1EB] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200"
            >
              {t('news.retry')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Sélectionner le contenu selon la langue actuelle
  const getContentByLanguage = (field) => {
    if (currentLanguage === 'en') {
      return oddSection[`${field}_en`] || oddSection[`${field}_fr`] || '';
    }
    return oddSection[`${field}_fr`] || oddSection[`${field}_en`] || '';
  };

  // Récupérer le contenu traduit
  const title = getContentByLanguage('title');
  const leftPanelImage = getContentByLanguage('left_panel_image');
  const leftPanelDescription = getContentByLanguage('left_panel_description');
  const rightPanelImage = getContentByLanguage('right_panel_image');
  const rightPanelDescription = getContentByLanguage('right_panel_description');

  return (
    <section className="w-full relative bg-gradient-to-br from-[#07D1EB] via-[#089556] to-[#0ba8c7] mt-8 sm:mt-12 overflow-hidden">
      
      {/* Enhanced Molecular Background Pattern */}
      <div className="absolute inset-0 opacity-[0.2]">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 2px, transparent 2px),
            radial-gradient(circle at 80% 70%, rgba(255,255,255,0.6) 3px, transparent 3px),
            radial-gradient(circle at 40% 80%, rgba(255,255,255,0.7) 2.5px, transparent 2.5px),
            radial-gradient(circle at 70% 20%, rgba(255,255,255,0.5) 3.5px, transparent 3.5px),
            radial-gradient(circle at 90% 50%, rgba(255,255,255,0.8) 2.8px, transparent 2.8px),
            radial-gradient(circle at 30% 60%, rgba(255,255,255,0.6) 2.2px, transparent 2.2px)
          `,
          backgroundSize: '180px 180px, 140px 140px, 200px 200px, 160px 160px, 170px 170px, 130px 130px'
        }}></div>
        
        {/* Enhanced Connection Lines */}
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(25deg, transparent 48%, rgba(255,255,255,0.3) 49%, rgba(255,255,255,0.3) 51%, transparent 52%),
            linear-gradient(-25deg, transparent 48%, rgba(255,255,255,0.2) 49%, rgba(255,255,255,0.2) 51%, transparent 52%),
            linear-gradient(60deg, transparent 48%, rgba(255,255,255,0.25) 49%, rgba(255,255,255,0.25) 51%, transparent 52%)
          `,
          backgroundSize: '320px 320px, 280px 280px, 350px 350px',
          opacity: '0.4'
        }}></div>
      </div>

      {/* Enhanced Floating Animated Molecules */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/6 left-1/5 w-3 h-3 sm:w-4 sm:h-4 bg-white rounded-full opacity-20 animate-pulse shadow-lg shadow-white/30"></div>
        <div className="absolute top-2/3 right-1/5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full opacity-30 animate-bounce shadow-lg shadow-white/40"></div>
        <div className="absolute top-1/2 left-3/4 w-3 h-3 sm:w-3.5 sm:h-3.5 bg-white rounded-full opacity-25 animate-pulse shadow-lg shadow-white/35"></div>
        <div className="absolute top-1/4 right-1/3 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full opacity-20 animate-bounce shadow-lg shadow-white/30"></div>
        <div className="absolute bottom-1/6 left-1/6 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full opacity-35 animate-pulse shadow-lg shadow-white/40"></div>
        <div className="absolute top-3/4 right-1/6 w-2.5 h-2.5 sm:w-2.8 sm:h-2.8 bg-white rounded-full opacity-25 animate-bounce shadow-lg shadow-white/35"></div>
      </div>

      {/* Additional Depth Layer */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 relative z-10">
        <h2 className="text-center text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-6 sm:mb-8 leading-tight max-w-4xl mx-auto drop-shadow-lg px-2">
          {title}
        </h2>
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          {/* Panneau gauche */}
          <div className="w-full lg:w-1/2 relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group">
            <div className="bg-white/95 backdrop-blur-sm p-3 sm:p-4 border border-white/20">
              <div className="aspect-[16/9] overflow-hidden rounded-lg">
                 <Link to="#">
                  <img
                    src={leftPanelImage}
                    alt="Panneau gauche ODD"
                    className="w-full h-full object-cover scale-110 transition-transform duration-500 group-hover:scale-105 filter group-hover:brightness-110"
                    onError={(e) => {
                      console.error('Erreur de chargement de l\'image gauche:', leftPanelImage);
                      e.target.src = '/section/section1.png'; // Image de fallback
                    }}
                  />
                </Link>
              </div>
              {leftPanelDescription && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 mt-1">{leftPanelDescription}</p>
                </div>
              )}
            </div>
          </div>

          {/* Panneau droit */}
          <div className="w-full lg:w-1/2 relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group">
            <div className="bg-white/95 backdrop-blur-sm p-3 sm:p-4 border border-white/20">
              <div className="aspect-[16/9] overflow-hidden rounded-lg">
                <Link to="#">
                  <img
                    src={rightPanelImage}
                    alt="Panneau droit ODD"
                    className="w-full h-full object-contain scale-85 transition-transform duration-500 group-hover:scale-105 filter group-hover:brightness-110"
                    onError={(e) => {
                      console.error('Erreur de chargement de l\'image droite:', rightPanelImage);
                      e.target.src = '/section/section2.png'; // Image de fallback
                    }}
                  />
                </Link>
              </div>
              {rightPanelDescription && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500 mt-1">{rightPanelDescription}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ODDSection;